import { BrowserRouter, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { SnackbarProvider } from "notistack";
import { HelmetProvider } from "react-helmet-async";
import { Box } from "@mui/material";
import ThemeProvider from "./theme";
import { AuthProvider } from "./auth/FirebaseContext";
import { store, persistor } from "./redux/store";
import Router from "./routes";
import Header from "./components/Header";
import MaintenancePage from "./pages/MaintenancePage";
import {
    fetchMaintenanceState,
    isAllowedDuringMaintenance,
    DEFAULT_TITLE,
    DEFAULT_MESSAGE,
} from "./services/maintenance";
import { MAINTENANCE_FORCED } from "./config";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function AppContent() {
    const location = useLocation();
    const isHome = location.pathname === "/";
    // Ulaz iz mobilne aplikacije (WebView) — bez web zaglavlja/menija da korisnik
    // ne može da izađe iz toka plaćanja i luta po web sajtu.
    const isEmbedded = location.pathname.startsWith("/placanje/m/");

    // Kad je build trajno u režimu održavanja, ne čeka se mrežni odgovor — inače
    // bi korisnik na trenutak video sajt i mogao da krene u kupovinu.
    const [maintenance, setMaintenance] = useState(
        MAINTENANCE_FORCED
            ? { enabled: true, title: DEFAULT_TITLE, message: DEFAULT_MESSAGE }
            : null
    );

    useEffect(() => {
        let cancelled = false;
        fetchMaintenanceState().then((state) => {
            if (!cancelled) setMaintenance(state);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (maintenance?.enabled && !isAllowedDuringMaintenance(location.pathname)) {
        return <MaintenancePage title={maintenance.title} message={maintenance.message} />;
    }

    return (
        <>
            <ScrollToTop />
            {!isEmbedded && <Header />}
            {!isHome && !isEmbedded && <Box sx={{ height: 64 }} />}
            {/* Glavni sadržaj mora imati <main> — čitač ekrana tako preskače zaglavlje. */}
            <Box component="main">
                <Router />
            </Box>
        </>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <BrowserRouter>
                        <AuthProvider>
                            <ThemeProvider>
                                <SnackbarProvider
                                    maxSnack={3}
                                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <AppContent />
                                </SnackbarProvider>
                            </ThemeProvider>
                        </AuthProvider>
                    </BrowserRouter>
                </PersistGate>
            </Provider>
        </HelmetProvider>
    );
}
